import { addBusinessDays, format } from "date-fns";

// The made-to-order setup of a product is held in one JSON the CMS carries on it: the layout it is
// customized through (`type`), the settings that customization is made of, and the copy around
// them. It drives which customizer the product page renders, and is kept on the cart line so the
// workshop knows what the customization it receives describes.
export function getMtoContent(product) {
  try {
    return JSON.parse(product?.madeToOrder?.settings?.internal?.content || "null");
  } catch (error) {
    return null;
  }
}

export function getMtoType(product) {
  return getMtoContent(product)?.type || null;
}

/**
 * Everything the made-to-order flows need from the CMS for one product: the settings it is
 * customized through, the preview it is drawn from, and the delivery timeline and final sale flag
 * it is sold under.
 * Returns null for a product that carries no (or unreadable) made-to-order settings.
 */
export function getMtoProductData(product) {
  const content = getMtoContent(product);
  if (!content?.settings) return null;

  const timeline = product?.madeToOrder?.madeToOrderTimelines;
  return {
    mtoType: content.type || null,
    mtoSettings: content,
    // The piece the customization is previewed on: the SVG its variants are cut out of, and the
    // images standing for them (the stone swatches, the chains framing a nameplate, the back of
    // the piece). A flow switching product mid-customization switches preview with it.
    svg: product?.madeToOrder?.productSvg?.file?.url || null,
    variantsPreview: product?.madeToOrder?.variantsPreview || null,
    timeline: timeline && timeline !== "null" ? timeline : null,
    isFinalSale: product?.madeToOrder?.isFinalSale ?? null,
  };
}

// The made-to-order timelines are a free-text range ("10-15 business days"). Returns the formatted
// delivery window counted in business days from today, or null when the text has no min and max.
export function getMtoDeliveryWindow(timelines, dateFormat = "MMM d") {
  const [minDays, maxDays] = (timelines || "").match(/\d+/g)?.map(Number) || [];
  if (minDays == null || maxDays == null) {
    return null;
  }

  const today = new Date();
  return {
    from: format(addBusinessDays(today, minDays), dateFormat),
    to: format(addBusinessDays(today, maxDays), dateFormat),
  };
}

export default getMtoType;
